import { assertNever } from "@redotech/util/type";
import type { StorefrontPlatform } from "./storefront-config";
export enum AttachmentStrategy {
  CHECK_OUT = "check-out",
  CHECKBOX = "checkbox",
  DOUBLE_CHECK_OUT = "double-check-out",
  DOUBLE_TOGGLE = "double-toggle",
  DROP_DOWN = "drop-down",
  SINGLE_TOGGLE = "single-toggle",
  CART_CARD = "cart-card",
  CHECKOUT_BUTTON_PRODUCT = "checkout-button-product",
  CHECKOUT_BUTTON_SHIPPING = "checkout-button-shipping",
  SHIPPING = "shipping",
  TOGGLE_CUSTOM_CHECKOUT_FEE = "toggle-custom-checkout-fee",
  ANY_WIDGET_CUSTOM_FEE = "any-widget-custom-fee",
  ANY_WIDGET_PRODUCT = "any-widget-product",
  NONE = "none",
}

export const bigcommerceAttachmentStrategies: AttachmentStrategy[] = [
  AttachmentStrategy.SINGLE_TOGGLE,
  AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE,
  AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
  AttachmentStrategy.ANY_WIDGET_PRODUCT,
  AttachmentStrategy.NONE,
];

export const BETA_ATTACHMENT_STRATEGIES = [
  AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
  AttachmentStrategy.ANY_WIDGET_PRODUCT,
];

export const DEPRECATED_ATTACHMENT_STRATEGIES = [
  AttachmentStrategy.CHECKBOX,
  AttachmentStrategy.DROP_DOWN,
  AttachmentStrategy.DOUBLE_TOGGLE,
];

export const NOT_SHOPIFY_COMPLIANT_ATTACHMENT_STRATEGIES = [
  AttachmentStrategy.CHECK_OUT,
  AttachmentStrategy.CHECKBOX,
  AttachmentStrategy.DOUBLE_CHECK_OUT,
  AttachmentStrategy.DOUBLE_TOGGLE,
  AttachmentStrategy.DROP_DOWN,
  AttachmentStrategy.SINGLE_TOGGLE,
  AttachmentStrategy.CART_CARD,
  AttachmentStrategy.SHIPPING,
];

/** Strategies that sell coverage as a merchant-configured custom fee instead of a product. */
export const CUSTOM_FEE_ATTACHMENT_STRATEGIES = [
  AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE,
  AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
];

/** Platforms whose clients can attach/remove a coverage custom fee. */
export const CUSTOM_FEE_PLATFORMS: StorefrontPlatform[] = [
  "bigcommerce",
  "commerce-cloud",
];

export const attachmentStrategyToLabel: Record<AttachmentStrategy, string> = {
  [AttachmentStrategy.CHECKBOX]: "PDP checkbox",
  [AttachmentStrategy.SINGLE_TOGGLE]: "Cart single toggle: product",
  [AttachmentStrategy.DOUBLE_TOGGLE]: "Cart double toggle",
  [AttachmentStrategy.CHECK_OUT]: "Checkout single checkbox",
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: "Checkout double checkbox",
  [AttachmentStrategy.CART_CARD]: "Cart card",
  [AttachmentStrategy.DROP_DOWN]: "Cart drop-down",
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]:
    "Cart checkout buttons: product",
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]:
    "Cart checkout buttons: shipping",
  [AttachmentStrategy.SHIPPING]: "Redo in shipping",
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]:
    "Cart single toggle: custom fee",
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]:
    "Checkout buttons + toggle: custom fee",
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: "Checkout buttons + toggle: product",
  [AttachmentStrategy.NONE]: "None (API / custom only)",
};

/** If we convert someone's attachment strategy, use shipping checkout buttons if they were doing the shipping attachment strategy. */
export const attachmentStrategyToCheckoutButtonVersion: Record<
  AttachmentStrategy,
  AttachmentStrategy
> = {
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]:
    AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING,
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]:
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.CART_CARD]: AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.DOUBLE_TOGGLE]:
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.SINGLE_TOGGLE]:
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.CHECK_OUT]: AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]:
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.CHECKBOX]: AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.DROP_DOWN]: AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.SHIPPING]: AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]:
    AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING,
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]:
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]:
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
  [AttachmentStrategy.NONE]: AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
};

export const isAttachmentStrategyUsingCheckoutButtons: Record<
  AttachmentStrategy,
  boolean
> = {
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]: true,
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]: true,
  [AttachmentStrategy.CART_CARD]: false,
  [AttachmentStrategy.DOUBLE_TOGGLE]: false,
  [AttachmentStrategy.SINGLE_TOGGLE]: false,
  [AttachmentStrategy.CHECK_OUT]: false,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: false,
  [AttachmentStrategy.CHECKBOX]: false,
  [AttachmentStrategy.DROP_DOWN]: false,
  [AttachmentStrategy.SHIPPING]: false,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]: false,
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: true,
  [AttachmentStrategy.NONE]: false,
};

export const singleCartToggleAttachmentStrategies = [
  AttachmentStrategy.SINGLE_TOGGLE,
  AttachmentStrategy.CART_CARD,
  AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE,
  AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
  AttachmentStrategy.ANY_WIDGET_PRODUCT,
];

export const doesAttachmentStrategyRenderInCart: Record<
  AttachmentStrategy,
  boolean
> = {
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]: true,
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]: true,
  [AttachmentStrategy.CART_CARD]: true,
  [AttachmentStrategy.DOUBLE_TOGGLE]: true,
  [AttachmentStrategy.SINGLE_TOGGLE]: true,
  [AttachmentStrategy.CHECK_OUT]: false,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: false,
  [AttachmentStrategy.CHECKBOX]: false,
  [AttachmentStrategy.DROP_DOWN]: false,
  [AttachmentStrategy.SHIPPING]: false,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: true,
  [AttachmentStrategy.NONE]: false,
};

export const isAttachmentStrategyCheckoutExtensionEnabled: Record<
  AttachmentStrategy,
  boolean
> = {
  [AttachmentStrategy.CHECK_OUT]: true,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: true,
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]: true,
  [AttachmentStrategy.SHIPPING]: true,
  [AttachmentStrategy.CHECKBOX]: false,
  [AttachmentStrategy.SINGLE_TOGGLE]: false,
  [AttachmentStrategy.DOUBLE_TOGGLE]: false,
  [AttachmentStrategy.CART_CARD]: false,
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]: false,
  [AttachmentStrategy.DROP_DOWN]: false,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]: false,
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]: false,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: false,
  [AttachmentStrategy.NONE]: false,
};

/** Which surfaces a strategy turns on and off, for arms and Go live. */
export interface AttachmentStrategySurfaces {
  cartToggle: boolean;
  checkoutExtension: boolean;
}

/**
 * The cart toggle and checkout extension an arm's strategy renders on, or null
 * for a strategy that keeps the team's.
 */
export function attachmentStrategySurfaces(
  strategy: AttachmentStrategy,
): AttachmentStrategySurfaces | null {
  switch (strategy) {
    case AttachmentStrategy.CHECKBOX:
      return { cartToggle: false, checkoutExtension: false };
    case AttachmentStrategy.SINGLE_TOGGLE:
    case AttachmentStrategy.DOUBLE_TOGGLE:
    case AttachmentStrategy.CART_CARD:
    case AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT:
    case AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING:
      return { cartToggle: true, checkoutExtension: false };
    case AttachmentStrategy.CHECK_OUT:
    case AttachmentStrategy.DOUBLE_CHECK_OUT:
      return { cartToggle: false, checkoutExtension: true };
    case AttachmentStrategy.DROP_DOWN:
    case AttachmentStrategy.SHIPPING:
    case AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE:
    case AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE:
    case AttachmentStrategy.ANY_WIDGET_PRODUCT:
    case AttachmentStrategy.NONE:
      return null;
    default:
      return assertNever(strategy);
  }
}

export const doesAttachmentStrategyUseCustomFee: Record<
  AttachmentStrategy,
  boolean
> = {
  [AttachmentStrategy.CHECK_OUT]: false,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: false,
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]: false,
  [AttachmentStrategy.SHIPPING]: false,
  [AttachmentStrategy.CHECKBOX]: false,
  [AttachmentStrategy.SINGLE_TOGGLE]: false,
  [AttachmentStrategy.DOUBLE_TOGGLE]: false,
  [AttachmentStrategy.CART_CARD]: false,
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]: false,
  [AttachmentStrategy.DROP_DOWN]: false,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: false,
  [AttachmentStrategy.NONE]: false,
};

export const attachmentStrategyNeedsToConfigureToggles: Record<
  AttachmentStrategy,
  boolean
> = {
  [AttachmentStrategy.CHECK_OUT]: true,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: true,
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]: false,
  [AttachmentStrategy.SHIPPING]: false,
  [AttachmentStrategy.CHECKBOX]: false,
  [AttachmentStrategy.DOUBLE_TOGGLE]: true,
  [AttachmentStrategy.SINGLE_TOGGLE]: true,
  [AttachmentStrategy.CART_CARD]: true,
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]: false,
  [AttachmentStrategy.DROP_DOWN]: false,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: true,
  [AttachmentStrategy.NONE]: false,
};

export const isAnyWidgetAttachmentStrategy: Record<
  AttachmentStrategy,
  boolean
> = {
  [AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE]: true,
  [AttachmentStrategy.ANY_WIDGET_PRODUCT]: true,
  [AttachmentStrategy.CHECK_OUT]: false,
  [AttachmentStrategy.DOUBLE_CHECK_OUT]: false,
  [AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING]: false,
  [AttachmentStrategy.SHIPPING]: false,
  [AttachmentStrategy.CHECKBOX]: false,
  [AttachmentStrategy.DOUBLE_TOGGLE]: false,
  [AttachmentStrategy.SINGLE_TOGGLE]: false,
  [AttachmentStrategy.CART_CARD]: false,
  [AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT]: false,
  [AttachmentStrategy.DROP_DOWN]: false,
  [AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE]: false,
  [AttachmentStrategy.NONE]: false,
};

/** Strategies offerable for a storefront platform, given its split-products setting. */
export function attachmentStrategyDropdownOptions(
  splitProducts: boolean,
  platform: StorefrontPlatform | undefined,
): AttachmentStrategy[] {
  if (platform === "bigcommerce") {
    return bigcommerceAttachmentStrategies;
  }

  const optionsWithoutSplittingProducts = [
    AttachmentStrategy.CHECKBOX,
    AttachmentStrategy.SINGLE_TOGGLE,
    AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE,
    AttachmentStrategy.CART_CARD,
    AttachmentStrategy.CHECK_OUT,
    AttachmentStrategy.SHIPPING,
    AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
    AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING,
    AttachmentStrategy.ANY_WIDGET_CUSTOM_FEE,
    AttachmentStrategy.ANY_WIDGET_PRODUCT,
    AttachmentStrategy.NONE,
  ];

  const optionsAvailableBasedOnSplitProductsSetting = splitProducts
    ? [
        ...optionsWithoutSplittingProducts,
        AttachmentStrategy.DOUBLE_TOGGLE,
        AttachmentStrategy.DOUBLE_CHECK_OUT,
      ]
    : optionsWithoutSplittingProducts;

  // Commerce Cloud sells coverage via the same custom-fee strategies as
  // BigCommerce, so keep them; other platforms never offer a custom fee.
  if (platform && CUSTOM_FEE_PLATFORMS.includes(platform)) {
    return optionsAvailableBasedOnSplitProductsSetting;
  }

  return optionsAvailableBasedOnSplitProductsSetting.filter(
    (strategy) => !CUSTOM_FEE_ATTACHMENT_STRATEGIES.includes(strategy),
  );
}
